import { Formik, useFormikContext } from 'formik'
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import { Form } from '../../../../components/formik-component'
import {
  FullMeetingFragment,
  MeetingStepStatus,
  MeetingStepViewId,
  SelfreflectionInput,
  SelfreflectionQuestion,
  SelfreflectionQuestionBoolean,
  SelfReflectionQuestionFragment,
  SelfReflectionQuestionType,
} from '../../../../types'

import selfreflectionService from '../../../appraisal/selfreflection/selfreflectionService'
import { SelfReflectionTopicCardsEdit } from '../../selfreflection/SelfreflectionTopicCardsEdit'
import useSelfreflections from '../../selfreflection/useSelfreflections'
import { AppraisalStepBtns } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { AppraisalFlowContext } from '../shared/AppraisalFlowProvider'
import { updateMeetingStep } from '../shared/AppraisalMeetingService'
import AppraisalPageDescription from '../shared/AppraisalPageDescription'

interface Props extends AppraisalStepBtns {
  meeting: FullMeetingFragment
}

const EmployeePreparationSelfReflection: FC<Props> = ({ backBtn, nextBtn, meeting }) => {
  const [t] = useTranslation()
  const { meetingTypeSteps } = useContext(AppraisalFlowContext)
  const currentStep = meetingTypeSteps.find((step) => step.viewId === MeetingStepViewId.SelfReflection)
  const [stepStatus] = useState<MeetingStepStatus>(MeetingStepStatus.Visited)
  const { data, error, loading } = useSelfreflections(meeting.employee.id, meeting.id)
  const ref = useRef<{ values: SelfreflectionQuestion[]; dirty: boolean }>({ values: [], dirty: false })

  const saveFormOnUnmount = useCallback(
    (values: SelfReflectionQuestionFragment[]): void => {
      if (ref.current.dirty) {
        for (const question of values) {
          const selfReflectionInput: SelfreflectionInput = {
            id: question.id,
            value: question.value,
            comment: question.comment,
          }
          selfreflectionService.updateSelfreflectionQuestionApi(meeting.employee.id, meeting.id, selfReflectionInput)
          if (question.type === SelfReflectionQuestionType.BooleanTick || question.type === SelfReflectionQuestionType.BooleanCheckbox) {
            for (const subItem of question.subItems) {
              const selfReflectionQuestionBooleanInput: SelfreflectionQuestionBoolean = {
                id: subItem.id,
                position: subItem.position,
                value: subItem.value,
              }
              selfreflectionService.updateSelfreflectionQuestionBooleanApi(
                meeting.employee.id,
                meeting.id,
                subItem.id,
                selfReflectionQuestionBooleanInput,
              )
            }
          }
        }
      }
      if (currentStep && currentStep.status !== MeetingStepStatus.Done) {
        const atLeastOneItemIsNotEmpty = values.some((question) => {
          if (question.comment) return true
          if (question.type === SelfReflectionQuestionType.BooleanTick || question.type === SelfReflectionQuestionType.BooleanCheckbox) {
            return question.subItems.some((item) => item.value)
          } else {
            return question.value
          }
        })
        updateMeetingStep(currentStep.id, atLeastOneItemIsNotEmpty ? MeetingStepStatus.Done : MeetingStepStatus.Visited)
      } else {
        /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
        updateMeetingStep(currentStep!.id, MeetingStepStatus.Done)
      }
    },
    [currentStep, meeting.employee.id, meeting.id],
  )

  if (loading || error || !data || !data.selfreflectionQuestions || !data.selfreflectionQuestions.length) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data || !data.selfreflectionQuestions} error={error} />
  }

  const initialValues = data.selfreflectionQuestions as SelfReflectionQuestionFragment[]

  const FormikHelper = () => {
    const { values, dirty } = useFormikContext<{ form: SelfreflectionQuestion[] }>()
    ref.current.values = values.form
    ref.current.dirty = dirty
    useEffect(() => {
      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        saveFormOnUnmount(ref.current.values)
      }
    }, [])

    return null
  }

  return (
    <>
      <MeetingBackground>
        <AppraisalPageDescription description={t('appraisalMeeting.stepSelfReflection.pageDescriptionEmployee')} />
        <Formik
          initialValues={{ form: initialValues }}
          onSubmit={(_, { setSubmitting }) => {
            setSubmitting(false)
          }}
          validateOnChange={false}
        >
          {({ values }) => {
            return (
              <>
                <Form>
                  <div data-e2e-selfreflection-entries>
                    <SelfReflectionTopicCardsEdit meeting={meeting} questions={values.form} />
                  </div>
                </Form>
                <FormikHelper />
              </>
            )
          }}
        </Formik>
      </MeetingBackground>
      <PerFlexRow justify="space-between">
        {backBtn && backBtn()}
        {nextBtn && nextBtn()}
      </PerFlexRow>

      <AppraisalFlowHelper stepViewId={MeetingStepViewId.SelfReflection} stepStatus={stepStatus} />
    </>
  )
}
export default EmployeePreparationSelfReflection
